import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { propertiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    availabilityStatus: '',
    propertyType: '',
    availableArea: ''
  });
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadProperties();
  }, [search, filters, pagination.current]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await propertiesAPI.getAll({
        search,
        ...filters,
        page: pagination.current
      });
      setProperties(response.data.properties);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Erro ao carregar imóveis');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deseja realmente excluir o imóvel "${name || 'Sem nome'}"?`)) {
      return;
    }

    try {
      await propertiesAPI.delete(id);
      toast.success('Imóvel excluído com sucesso');
      loadProperties();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao excluir imóvel';
      toast.error(message);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current: 1 }));
    loadProperties();
  };

  const getStatusColor = (status) => {
    const colors = {
      'Disponível': 'success',
      'Em Negociação': 'warning',
      'Em Assinatura do Contrato': 'info',
      'Contrato Assinado': 'primary',
      'Em Licenciamento': 'warning',
      'Licença Emitida': 'success',
      'Reservado': 'info',
      'Locado': 'primary',
      'Indisponível': 'danger'
    };
    return colors[status] || 'secondary';
  };

  const getCadastroStatusColor = (status) => {
    const colors = {
      'Pendente': 'warning',
      'Em Análise': 'info',
      'Aprovado': 'success',
      'Rejeitado': 'danger'
    };
    return colors[status] || 'secondary';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Imóveis</h1>
          <p className="page-subtitle">Gerencie os imóveis cadastrados para locação de ERBs</p>
        </div>
        <Link to="/imoveis/novo" className="btn btn-primary">
          ➕ Novo Imóvel
        </Link>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="filters-bar">
        <input
          type="text"
          className="form-input filter-input"
          placeholder="Buscar por nome, cidade ou bairro..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select filter-select"
          value={filters.availabilityStatus}
          onChange={(e) => setFilters(prev => ({ ...prev, availabilityStatus: e.target.value }))}
        >
          <option value="">Todos os status</option>
          <option value="Disponível">Disponível</option>
          <option value="Em Negociação">Em Negociação</option>
          <option value="Em Assinatura do Contrato">Em Assinatura do Contrato</option>
          <option value="Contrato Assinado">Contrato Assinado</option>
          <option value="Em Licenciamento">Em Licenciamento</option>
          <option value="Licença Emitida">Licença Emitida</option>
          <option value="Reservado">Reservado</option>
          <option value="Locado">Locado</option>
          <option value="Indisponível">Indisponível</option>
        </select>
        <select
          className="form-select filter-select"
          value={filters.propertyType}
          onChange={(e) => setFilters(prev => ({ ...prev, propertyType: e.target.value }))}
        >
          <option value="">Todos os tipos</option>
          <option value="Terreno">Terreno</option>
          <option value="Cobertura">Cobertura</option>
          <option value="Laje">Laje</option>
          <option value="Telhado">Telhado</option>
          <option value="Torre Existente">Torre Existente</option>
          <option value="Outro">Outro</option>
        </select>
        <select
          className="form-select filter-select"
          value={filters.availableArea}
          onChange={(e) => setFilters(prev => ({ ...prev, availableArea: e.target.value }))}
        >
          <option value="">Todas as áreas</option>
          <option value="10x10">10x10</option>
          <option value="10x15">10x15</option>
          <option value="15x15">15x15</option>
          <option value="15x20">15x20</option>
          <option value="20x20">20x20</option>
        </select>
        <button type="submit" className="btn btn-secondary">
          🔍 Buscar
        </button>
      </form>

      {/* Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : properties.length > 0 ? (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Identificação</th>
                      <th>Proprietário</th>
                      <th>Tipo</th>
                      <th>Área</th>
                      <th>Localização</th>
                      <th>Disponibilidade</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((property) => (
                      <tr key={property._id}>
                        <td>
                          <Link 
                            to={`/imoveis/${property._id}`}
                            style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}
                          >
                            {property.propertyName || 'Sem nome'}
                          </Link>
                        </td>
                        <td>{property.owner?.name || '-'}</td>
                        <td>{property.propertyType}</td>
                        <td>{property.availableArea}</td>
                        <td>
                          {property.address?.city}/{property.address?.state}
                        </td>
                        <td>
                          <span className={`badge badge-${getStatusColor(property.availabilityStatus)}`}>
                            {property.availabilityStatus}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${getCadastroStatusColor(property.status)}`}>
                            {property.status}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link 
                              to={`/imoveis/${property._id}`}
                              className="btn btn-sm btn-outline"
                              title="Ver detalhes"
                            >
                              👁️
                            </Link>
                            <Link 
                              to={`/imoveis/${property._id}/editar`}
                              className="btn btn-sm btn-outline"
                              title="Editar"
                            >
                              ✏️
                            </Link>
                            {hasPermission('Administrador', 'Analista') && (
                              <button
                                onClick={() => handleDelete(property._id, property.propertyName)}
                                className="btn btn-sm btn-danger"
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.current === 1}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  >
                    ◀
                  </button>
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.current - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`pagination-btn ${pagination.current === pageNum ? 'active' : ''}`}
                        onClick={() => setPagination(prev => ({ ...prev, current: pageNum }))}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="pagination-btn"
                    disabled={pagination.current === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  >
                    ▶
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🏢</div>
              <div className="empty-state-title">Nenhum imóvel encontrado</div>
              <div className="empty-state-text">
                {search || filters.availabilityStatus || filters.propertyType || filters.availableArea
                  ? 'Tente ajustar os filtros de busca'
                  : 'Cadastre o primeiro imóvel para começar'
                }
              </div>
              {!search && !filters.availabilityStatus && !filters.propertyType && !filters.availableArea && (
                <Link to="/imoveis/novo" className="btn btn-primary" style={{ marginTop: '16px' }}>
                  ➕ Cadastrar Imóvel
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Properties;
